import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { FormField } from '@/components/FormField';
import {
  defaultTemplatesService,
  DefaultTemplate,
  DefaultTemplatePayload,
  HeaderType,
  TemplateCategory,
  TemplateButton,
} from '@/services/default-templates.service';
import {
  Phone, Video, MoreVertical, ChevronLeft, Smile, Paperclip, Mic,
  Image as ImageIcon, FileText, MapPin, Reply, Link as LinkIcon, Copy,
  Plus, Trash2, Sparkles,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ---------------------------------------------------------------------------
// Inline WhatsApp preview (mirrors slightercrm's TemplatePreview)
// ---------------------------------------------------------------------------

function substitute(text: string, samples: Record<string, string>): string {
  return text.replace(/\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g, (_m, name: string) =>
    samples[name] !== undefined && samples[name] !== '' ? samples[name] : `{{${name}}}`,
  );
}

function timeNow(): string {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const WAPreview: React.FC<{
  headerType: HeaderType;
  headerText?: string;
  headerImageUrl?: string;
  headerVideoUrl?: string;
  headerDocumentUrl?: string;
  headerDocumentFilename?: string;
  headerLocation?: { latitude?: number; longitude?: number; name?: string; address?: string };
  body: string;
  footer?: string;
  buttons: TemplateButton[];
  samples: Record<string, string>;
}> = (p) => {
  const time = timeNow();
  const ht = p.headerType;
  return (
    <div className="relative mx-auto" style={{ width: 300 }}>
      <div className="relative rounded-[32px] bg-[#0b0b0c] p-2 shadow-xl ring-1 ring-black/40">
        <div className="rounded-[24px] overflow-hidden bg-[#0a1014]">
          <div className="relative flex items-center justify-center bg-black h-5">
            <div className="w-20 h-4 rounded-b-2xl bg-black absolute top-0" />
          </div>
          <div className="flex items-center justify-between bg-[#075E54] px-3 pt-1.5 pb-1.5 text-[9px] font-medium text-white/85">
            <span>{time}</span>
            <span className="flex items-center gap-1">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-white/85" />
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-white/85" />
            </span>
          </div>
          <div className="flex items-center gap-2 bg-[#075E54] px-2.5 py-2 text-white">
            <ChevronLeft size={15} />
            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#128C7E] text-[9px] font-bold">US</div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[11px] font-semibold">Preview</div>
            </div>
            <Video size={13} className="opacity-80" />
            <Phone size={12} className="opacity-80" />
            <MoreVertical size={13} className="opacity-80" />
          </div>
          <div
            className="relative px-1.5 py-2 min-h-[300px]"
            style={{ background: '#ECE5DD' }}
          >
            <div className="flex justify-center pb-1.5">
              <span className="rounded-md bg-white/85 px-2 py-0.5 text-[9px] font-medium text-black/55 shadow-sm">TODAY</span>
            </div>
            <div className="flex justify-start">
              <div className="relative max-w-[90%] rounded-lg rounded-tl-sm bg-white shadow-sm">
                <span className="absolute -left-[5px] top-0 h-3 w-3 bg-white" style={{ clipPath: 'polygon(100% 0, 0 0, 100% 100%)' }} />
                {ht === 'IMAGE' && (p.headerImageUrl
                  ? <div className="overflow-hidden rounded-t-lg"><img src={p.headerImageUrl} alt="" className="block aspect-video w-full object-cover" onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }} /></div>
                  : <div className="m-1 flex aspect-video items-center justify-center rounded-md bg-black/5 text-black/40"><ImageIcon size={22} /></div>
                )}
                {ht === 'VIDEO' && (
                  <div className="m-1 flex aspect-video items-center justify-center rounded-md bg-black/5 text-black/40">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-black/30 text-white text-xs">▶</div>
                  </div>
                )}
                {ht === 'DOCUMENT' && (
                  <div className="m-1 flex items-center gap-2 rounded-md bg-black/5 p-2 text-black">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#3B71CA] text-white"><FileText size={14} /></div>
                    <div className="min-w-0 flex-1"><div className="truncate text-[10px] font-semibold">{p.headerDocumentFilename || 'document.pdf'}</div></div>
                  </div>
                )}
                {ht === 'LOCATION' && (
                  <div className="m-1 overflow-hidden rounded-md bg-[#dcdcc7]">
                    <div className="relative h-20 bg-gradient-to-br from-[#a3c98e] via-[#cad8a5] to-[#dcdcc7]">
                      <MapPin className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-[#cc3a36]" size={22} />
                    </div>
                    <div className="bg-white px-2 py-1">
                      <div className="text-[10px] font-semibold text-black truncate">{p.headerLocation?.name || 'Location'}</div>
                    </div>
                  </div>
                )}
                {ht === 'TEXT' && p.headerText && (
                  <div className="px-2.5 pt-2 text-[12px] font-bold leading-tight text-black">
                    {substitute(p.headerText, p.samples)}
                  </div>
                )}
                <div className="px-2.5 pt-1.5 pb-1 text-[11.5px] leading-snug text-black whitespace-pre-wrap break-words">
                  {p.body ? substitute(p.body, p.samples) : <span className="text-black/40">Write body…</span>}
                </div>
                {p.footer && <div className="px-2.5 pb-1 text-[10px] leading-snug text-black/55">{p.footer}</div>}
                <div className="flex items-center justify-end gap-1 px-2 pb-1 pt-0.5">
                  <span className="text-[9px] text-black/45">{time}</span>
                </div>
                {p.buttons.length > 0 && (
                  <div className="border-t border-black/10">
                    {p.buttons.map((b, i) => {
                      let icon: React.ReactNode = null;
                      if (b.type === 'URL') icon = <LinkIcon size={11} className="text-[#3490e6]" />;
                      if (b.type === 'PHONE') icon = <Phone size={11} className="text-[#3490e6]" />;
                      if (b.type === 'QUICK_REPLY') icon = <Reply size={11} className="text-[#3490e6]" />;
                      if (b.type === 'COPY_CODE') icon = <Copy size={11} className="text-[#3490e6]" />;
                      return (
                        <div key={i} className="flex items-center justify-center gap-1.5 border-b border-black/10 last:border-b-0 py-1.5 text-[11px] font-medium text-[#3490e6]">
                          {icon}<span className="truncate">{b.text || '(button text)'}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 bg-[#F0F0F0] px-1.5 py-1.5">
            <div className="flex flex-1 items-center gap-1.5 rounded-full bg-white px-2.5 py-1">
              <Smile size={12} className="text-black/40" />
              <span className="flex-1 text-[10px] text-black/40">Type a message</span>
              <Paperclip size={12} className="text-black/40" />
            </div>
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#075E54] text-white shadow-sm">
              <Mic size={11} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Form state
// ---------------------------------------------------------------------------

const EMPTY: DefaultTemplatePayload = {
  title: '',
  description: '',
  tags: [],
  thumbnailUrl: '',
  name: '',
  language: 'en',
  category: 'marketing',
  headerType: 'NONE',
  headerText: '',
  headerImageUrl: '',
  headerVideoUrl: '',
  headerDocumentUrl: '',
  headerDocumentFilename: '',
  body: '',
  footer: '',
  buttons: [],
  previewSamples: {},
  isActive: false,
};

const HEADER_TYPES: { value: HeaderType; label: string }[] = [
  { value: 'NONE', label: 'None' },
  { value: 'TEXT', label: 'Text' },
  { value: 'IMAGE', label: 'Image' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'DOCUMENT', label: 'Document' },
  { value: 'LOCATION', label: 'Location' },
];

const BUTTON_TYPES: { value: TemplateButton['type']; label: string }[] = [
  { value: 'URL', label: 'URL' },
  { value: 'PHONE', label: 'Call' },
  { value: 'QUICK_REPLY', label: 'Quick Reply' },
  { value: 'COPY_CODE', label: 'Copy Code' },
];

export default function DefaultTemplateForm() {
  const { uid } = useParams<{ uid: string }>();
  const nav = useNavigate();
  const editing = !!uid;

  const [form, setForm] = useState<DefaultTemplatePayload>(EMPTY);
  const [tagInput, setTagInput] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!editing) return;
    defaultTemplatesService.get(uid!).then((t: DefaultTemplate) => {
      setForm({
        title: t.title,
        description: t.description ?? '',
        tags: t.tags,
        thumbnailUrl: t.thumbnailUrl ?? '',
        name: t.name,
        language: t.language,
        category: t.category,
        headerType: t.headerType ?? 'NONE',
        headerText: t.headerText ?? '',
        headerImageUrl: t.headerImageUrl ?? '',
        headerVideoUrl: t.headerVideoUrl ?? '',
        headerDocumentUrl: t.headerDocumentUrl ?? '',
        headerDocumentFilename: t.headerDocumentFilename ?? '',
        headerLocation: t.headerLocation,
        body: t.body,
        footer: t.footer ?? '',
        buttons: t.buttons,
        previewSamples: t.previewSamples,
        isActive: t.isActive,
      });
    });
  }, [editing, uid]);

  const set = <K extends keyof DefaultTemplatePayload>(k: K, v: DefaultTemplatePayload[K]) =>
    setForm((s) => ({ ...s, [k]: v }));

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (t && !form.tags?.includes(t)) {
      setForm((s) => ({ ...s, tags: [...(s.tags ?? []), t] }));
    }
    setTagInput('');
  };

  const removeTag = (tag: string) =>
    setForm((s) => ({ ...s, tags: (s.tags ?? []).filter((t) => t !== tag) }));

  const addButton = (type: TemplateButton['type']) =>
    setForm((s) => ({
      ...s,
      buttons: [...(s.buttons ?? []), { type, text: '', ...(type === 'URL' ? { url: '' } : type === 'PHONE' ? { phone: '' } : type === 'COPY_CODE' ? { url: '' } : {}) }],
    }));

  const updateButton = (i: number, patch: Partial<TemplateButton>) =>
    setForm((s) => ({
      ...s,
      buttons: (s.buttons ?? []).map((b, idx) => (idx === i ? { ...b, ...patch } : b)),
    }));

  const removeButton = (i: number) =>
    setForm((s) => ({ ...s, buttons: (s.buttons ?? []).filter((_, idx) => idx !== i) }));

  const save = async (publish: boolean) => {
    setBusy(true);
    try {
      const payload: DefaultTemplatePayload = {
        ...form,
        isActive: publish,
        thumbnailUrl: form.thumbnailUrl || undefined,
        description: form.description || undefined,
        headerText: form.headerType === 'TEXT' ? form.headerText : undefined,
        headerImageUrl: form.headerType === 'IMAGE' ? form.headerImageUrl : undefined,
        headerVideoUrl: form.headerType === 'VIDEO' ? form.headerVideoUrl : undefined,
        headerDocumentUrl: form.headerType === 'DOCUMENT' ? form.headerDocumentUrl : undefined,
        headerDocumentFilename: form.headerType === 'DOCUMENT' ? form.headerDocumentFilename : undefined,
        headerLocation: form.headerType === 'LOCATION' ? form.headerLocation : undefined,
      };
      if (editing) {
        await defaultTemplatesService.update(uid!, payload);
        toast.success('Saved');
      } else {
        const created = await defaultTemplatesService.create(payload);
        toast.success('Created');
        nav(`/default-templates/${created.defaultTemplateUid}`);
        return;
      }
    } catch {
      // error toast handled by api interceptor
    } finally {
      setBusy(false);
    }
  };

  const samples = form.previewSamples ?? {};

  return (
    <>
      <Topbar
        title={editing ? 'Edit Default Template' : 'New Default Template'}
        breadcrumb="Catalog / Default Templates"
      />
      <div className="p-6 flex gap-6 items-start">
        {/* ── Left column: form ── */}
        <div className="flex-1 min-w-0 space-y-5">
          {/* Curation metadata */}
          <section className="bg-surface border border-border rounded-xl p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 dark:bg-amber-900/30">
                <Sparkles size={15} className="text-amber-500" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-text-primary">Gallery metadata</h3>
                <p className="text-xs text-text-muted">How this template appears in the gallery.</p>
              </div>
            </div>
            <FormField label="Title" required>
              <Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Order confirmation" maxLength={128} />
            </FormField>
            <FormField label="Description">
              <Textarea value={form.description ?? ''} onChange={(e) => set('description', e.target.value)} placeholder="A friendly confirmation sent after a purchase." rows={2} maxLength={512} />
            </FormField>
            <FormField label="Tags">
              <div className="flex flex-wrap gap-1.5 mb-2">
                {(form.tags ?? []).map((tag) => (
                  <span key={tag} className="flex items-center gap-1 rounded-full bg-surface-alt border border-border px-2 py-0.5 text-xs text-text-primary">
                    {tag}
                    <button type="button" onClick={() => removeTag(tag)} className="text-text-muted hover:text-danger leading-none">×</button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(); } }}
                  placeholder="e-commerce, otp, promo…"
                />
                <Button type="button" variant="secondary" size="sm" onClick={addTag}>Add</Button>
              </div>
            </FormField>
            <FormField label="Thumbnail URL">
              <Input value={form.thumbnailUrl ?? ''} onChange={(e) => set('thumbnailUrl', e.target.value)} placeholder="https://…/thumb.jpg" />
              {form.thumbnailUrl && (
                <div className="mt-2 w-32 rounded-lg overflow-hidden border border-border">
                  <img src={form.thumbnailUrl} alt="thumbnail" className="w-full aspect-video object-cover" onError={(e) => { (e.target as HTMLImageElement).style.opacity = '0.3'; }} />
                </div>
              )}
            </FormField>
          </section>

          {/* Content identity */}
          <section className="bg-surface border border-border rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-text-primary mb-4">Template identity</h3>
            <div className="grid grid-cols-3 gap-3">
              <FormField label="Name (snake_case)" required>
                <Input value={form.name} onChange={(e) => set('name', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))} placeholder="order_confirm" maxLength={512} />
              </FormField>
              <FormField label="Language" required>
                <Input value={form.language} onChange={(e) => set('language', e.target.value)} placeholder="en" maxLength={8} />
              </FormField>
              <FormField label="Category" required>
                <select
                  value={form.category}
                  onChange={(e) => set('category', e.target.value as TemplateCategory)}
                  className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="marketing">Marketing</option>
                  <option value="utility">Utility</option>
                  <option value="authentication">Authentication</option>
                </select>
              </FormField>
            </div>
          </section>

          {/* Header */}
          <section className="bg-surface border border-border rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-text-primary mb-3">Header</h3>
            <div className="flex flex-wrap gap-2 mb-4">
              {HEADER_TYPES.map((ht) => (
                <button
                  key={ht.value}
                  type="button"
                  onClick={() => set('headerType', ht.value)}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors ${
                    form.headerType === ht.value
                      ? 'border-primary bg-primary text-on-primary'
                      : 'border-border bg-surface text-text-secondary hover:border-primary'
                  }`}
                >
                  {ht.label}
                </button>
              ))}
            </div>
            {form.headerType === 'TEXT' && (
              <FormField label="Header text (max 60 chars)">
                <Input value={form.headerText ?? ''} onChange={(e) => set('headerText', e.target.value)} placeholder="Order #{{orderId}}" maxLength={60} />
              </FormField>
            )}
            {form.headerType === 'IMAGE' && (
              <FormField label="Image URL">
                <Input value={form.headerImageUrl ?? ''} onChange={(e) => set('headerImageUrl', e.target.value)} placeholder="https://…/image.jpg" />
              </FormField>
            )}
            {form.headerType === 'VIDEO' && (
              <FormField label="Video URL">
                <Input value={form.headerVideoUrl ?? ''} onChange={(e) => set('headerVideoUrl', e.target.value)} placeholder="https://…/video.mp4" />
              </FormField>
            )}
            {form.headerType === 'DOCUMENT' && (
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Document URL">
                  <Input value={form.headerDocumentUrl ?? ''} onChange={(e) => set('headerDocumentUrl', e.target.value)} placeholder="https://…/file.pdf" />
                </FormField>
                <FormField label="File name">
                  <Input value={form.headerDocumentFilename ?? ''} onChange={(e) => set('headerDocumentFilename', e.target.value)} placeholder="invoice.pdf" maxLength={255} />
                </FormField>
              </div>
            )}
            {form.headerType === 'LOCATION' && (
              <div className="grid grid-cols-2 gap-3">
                <FormField label="Latitude">
                  <Input type="number" step="any" value={form.headerLocation?.latitude ?? ''} onChange={(e) => set('headerLocation', { ...(form.headerLocation ?? {}), latitude: parseFloat(e.target.value) || undefined })} placeholder="25.2048" />
                </FormField>
                <FormField label="Longitude">
                  <Input type="number" step="any" value={form.headerLocation?.longitude ?? ''} onChange={(e) => set('headerLocation', { ...(form.headerLocation ?? {}), longitude: parseFloat(e.target.value) || undefined })} placeholder="55.2708" />
                </FormField>
                <FormField label="Place name">
                  <Input value={form.headerLocation?.name ?? ''} onChange={(e) => set('headerLocation', { ...(form.headerLocation ?? {}), name: e.target.value })} placeholder="Burj Khalifa" />
                </FormField>
                <FormField label="Address">
                  <Input value={form.headerLocation?.address ?? ''} onChange={(e) => set('headerLocation', { ...(form.headerLocation ?? {}), address: e.target.value })} placeholder="Downtown Dubai, UAE" />
                </FormField>
              </div>
            )}
          </section>

          {/* Body */}
          <section className="bg-surface border border-border rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-text-primary mb-3">Message body</h3>
            <FormField label="Body text" required>
              <Textarea value={form.body} onChange={(e) => set('body', e.target.value)} placeholder="Hi {{firstName}}, your order {{orderNumber}} is confirmed." rows={5} maxLength={1024} />
              <span className="block text-xs text-text-muted mt-1">{form.body.length} / 1024 · Use {'{{variableName}}'} for personalization</span>
            </FormField>
            <FormField label="Footer (optional, max 60 chars)">
              <Input value={form.footer ?? ''} onChange={(e) => set('footer', e.target.value)} placeholder="Reply STOP to unsubscribe" maxLength={60} />
            </FormField>
          </section>

          {/* Buttons */}
          <section className="bg-surface border border-border rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-text-primary">Buttons</h3>
              <div className="flex flex-wrap gap-1.5">
                {BUTTON_TYPES.map((bt) => (
                  <button
                    key={bt.value}
                    type="button"
                    onClick={() => addButton(bt.value)}
                    disabled={(form.buttons ?? []).length >= 10}
                    className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs font-medium text-text-secondary hover:border-primary hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Plus size={11} /> {bt.label}
                  </button>
                ))}
              </div>
            </div>
            {(form.buttons ?? []).length === 0 ? (
              <div className="rounded-lg border border-dashed border-border bg-surface-alt/50 px-4 py-5 text-center">
                <p className="text-xs text-text-muted">No buttons. Add URL, call, quick reply, or copy-code buttons above.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {(form.buttons ?? []).map((b, i) => (
                  <div key={i} className="rounded-xl border border-border bg-surface-alt/40 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-text-muted">{b.type}</span>
                      <button type="button" onClick={() => removeButton(i)} className="p-1 rounded-md text-text-muted hover:text-danger hover:bg-danger/10 transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <FormField label="Button text" required>
                        <Input value={b.text} maxLength={25} onChange={(e) => updateButton(i, { text: e.target.value })} placeholder="Visit website" />
                      </FormField>
                      {b.type === 'URL' && (
                        <FormField label="URL">
                          <Input value={b.url ?? ''} onChange={(e) => updateButton(i, { url: e.target.value })} placeholder="https://example.com" />
                        </FormField>
                      )}
                      {b.type === 'PHONE' && (
                        <FormField label="Phone (E.164)">
                          <Input value={b.phone ?? ''} onChange={(e) => updateButton(i, { phone: e.target.value })} placeholder="+971501234567" />
                        </FormField>
                      )}
                      {b.type === 'QUICK_REPLY' && (
                        <FormField label="Payload (optional)">
                          <Input value={b.payload ?? ''} onChange={(e) => updateButton(i, { payload: e.target.value })} placeholder="confirm_yes" />
                        </FormField>
                      )}
                      {b.type === 'COPY_CODE' && (
                        <FormField label="Code / placeholder">
                          <Input value={b.url ?? ''} onChange={(e) => updateButton(i, { url: e.target.value })} placeholder="{{code}}" />
                        </FormField>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Preview samples */}
          <section className="bg-surface border border-border rounded-xl p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-text-primary mb-3">Sample values (for preview)</h3>
            <p className="text-xs text-text-muted mb-3">Fill in example values for your template variables so companies can see a realistic preview.</p>
            <div className="grid grid-cols-2 gap-3">
              {extractVariables(form.body, form.headerText).map((v) => (
                <FormField key={v} label={`{{${v}}}`}>
                  <Input
                    value={samples[v] ?? ''}
                    onChange={(e) => set('previewSamples', { ...samples, [v]: e.target.value })}
                    placeholder={`Sample value for ${v}`}
                  />
                </FormField>
              ))}
            </div>
            {extractVariables(form.body, form.headerText).length === 0 && (
              <p className="text-xs text-text-muted">No variables detected yet. Use {'{{variableName}}'} in the body or header to create them.</p>
            )}
          </section>

          {/* Actions */}
          <div className="flex justify-end gap-3 pb-10">
            <Button variant="secondary" onClick={() => nav('/default-templates')}>Cancel</Button>
            <Button variant="secondary" disabled={busy} onClick={() => save(false)}>Save Draft</Button>
            <Button disabled={busy} onClick={() => save(true)}>
              {editing && form.isActive ? 'Save & Publish' : 'Publish'}
            </Button>
          </div>
        </div>

        {/* ── Right column: preview ── */}
        <div className="hidden xl:block w-[340px] shrink-0">
          <div className="sticky top-6">
            <div className="mb-3 flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30">
                <Phone size={13} className="text-green-600" />
              </div>
              <span className="text-sm font-semibold text-text-primary">WhatsApp preview</span>
            </div>
            <WAPreview
              headerType={form.headerType ?? 'NONE'}
              headerText={form.headerText}
              headerImageUrl={form.headerImageUrl}
              headerVideoUrl={form.headerVideoUrl}
              headerDocumentUrl={form.headerDocumentUrl}
              headerDocumentFilename={form.headerDocumentFilename}
              headerLocation={form.headerLocation}
              body={form.body}
              footer={form.footer}
              buttons={form.buttons ?? []}
              samples={samples}
            />
          </div>
        </div>
      </div>
    </>
  );
}

function extractVariables(...texts: (string | undefined)[]): string[] {
  const vars: string[] = [];
  const seen = new Set<string>();
  for (const text of texts) {
    if (!text) continue;
    const matches = Array.from(text.matchAll(/\{\{\s*([a-zA-Z][a-zA-Z0-9_]*)\s*\}\}/g));
    for (const m of matches) {
      if (!seen.has(m[1])) { seen.add(m[1]); vars.push(m[1]); }
    }
  }
  return vars;
}
